import { useQuery } from '@tanstack/react-query'
import { ExpertFeatures, ExpertLanguages, ParamsType } from '../types'
import { ExpertsRawData, ExpertsRead, ExpertsResponse } from '../interfaces'
import { fetchStrapi, getStrapiImage } from '@/libs'

const parseExperts = (expertsData: ExpertsRawData[]): ExpertsRead[] => {
  return expertsData.map((expert: ExpertsRawData) => {
    return {
      id: expert.id,
      name: expert.attributes.name,
      type: expert.attributes.type,
      description: expert.attributes.description,
      certification: expert.attributes.certification,
      email: expert.attributes.email,
      location: expert.attributes.location,
      languages: expert.attributes.languages.data.map(language => {
        return {
          value: language.attributes.value as ExpertLanguages,
          text: language.attributes.text,
        }
      }),
      features: expert.attributes.features.data.map(feature => {
        return {
          value: feature.attributes.value as ExpertFeatures,
          text: feature.attributes.text,
        }
      }),
      hubs: expert.attributes.hubs.map(hub => {
        return {
          ...hub,
          thumbinail: getStrapiImage(hub.thumbinail).src,
        }
      }),
      avatar: getStrapiImage(expert.attributes.avatar).src,
      socials: expert.attributes.socials,
    }
  })
}

const fetchExperts = async () =>
  await fetchStrapi<ExpertsResponse>({
    req: {
      url: '/api/experts',
      populate:
        'populate[hubs][populate][thumbinail][populate]=*&populate[features][populate]=*&populate[avatar][populate]=*&populate[languages][populate]=*&populate[socials][populate]=*',
    },
    tags: ['experts'],
  })

async function getExpertsList(): Promise<ExpertsRead[]> {
  return await fetchExperts().then(data => parseExperts(data && data.data ? data?.data : []))
}

function filter(data: ExpertsRead[], query: ParamsType) {
  return data.filter(item => {
    let isValid = true
    if (query.type && item.type !== query.type && item) {
      isValid = false
    }
    if (query.certification && item.certification !== query.certification && item) {
      isValid = false
    }
    if (query.languages && item.languages.findIndex(language => language.value === query.languages) === -1) {
      isValid = false
    }
    if (
      query.features &&
      query.features?.split(',').filter(ft => item.features.findIndex(expertFt => expertFt.value === ft) !== -1)
        .length < query.features?.split(',').length
    ) {
      isValid = false
    }
    if (query.q) {
      if (
        !item.name.toLowerCase().includes(query.q.toLowerCase()) &&
        !item.location.toLowerCase().includes(query.q.toLowerCase())
      ) {
        isValid = false
      }
    }
    return isValid
  })
}

export const useExpertsQuery = (params: ParamsType) => {
  return useQuery<ExpertsRead[]>({
    queryKey: ['experts', params],
    queryFn: () => getExpertsList().then(data => filter(data, params)),
    refetchOnMount: true,
  })
}
